import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

interface BentoTileProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  colSpan?: number;
  rowSpan?: number;
  onClick?: () => void;
  accentColor?: string;
  noPadding?: boolean;
  headerAction?: React.ReactNode;
}

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const BentoTile: React.FC<BentoTileProps> = ({
  children,
  title,
  subtitle,
  colSpan = 1,
  rowSpan = 1,
  onClick,
  accentColor,
  noPadding = false,
  headerAction,
}) => {
  return (
    <Box
      component={motion.div}
      variants={itemVariants}
      whileHover={onClick ? {
        scale: 1.02,
        transition: { duration: 0.2 },
      } : undefined}
      onClick={onClick}
      sx={{
        gridColumn: {
          xs: 'span 1',
          sm: `span ${Math.min(colSpan, 2)}`,
          md: `span ${colSpan}`,
        },
        gridRow: { xs: 'span 1', md: `span ${rowSpan}` },
        position: 'relative',
        borderRadius: { xs: '14px', md: '20px' },
        background: 'rgba(26, 26, 26, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: accentColor
            ? `${accentColor}40`
            : 'rgba(74, 158, 255, 0.2)',
          boxShadow: accentColor
            ? `0 8px 32px ${accentColor}20`
            : '0 8px 32px rgba(74, 158, 255, 0.1)',
        },
        '&::before': accentColor ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${accentColor}, transparent)`,
          opacity: 0.6,
        } : undefined,
      }}
    >
      {(title || subtitle || headerAction) && (
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: noPadding ? { xs: 1.5, md: 2 } : 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            {title && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  mb: subtitle ? 0.5 : 0,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerAction}
        </Box>
      )}
      <Box sx={{
        p: noPadding ? 0 : { xs: 2, md: 3 },
        pt: (title || subtitle) ? (noPadding ? 0 : { xs: 1.5, md: 2 }) : (noPadding ? 0 : { xs: 2, md: 3 })
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default BentoTile;
